import React, { useState } from 'react'; // NEW: Import useState
import { PriceProduct, PriceDetail } from '../types/costwand.d'; // Import PriceDetail
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.tsx';
import Pagination from './ui/Pagination.tsx';
import { Link } from 'react-router-dom';
import { Button } from './ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog.tsx'; // Import Dialog components
import { Spinner } from './ui/spinner.tsx'; // Import Spinner
import { apiClient } from '../api/apiClient.ts'; // NEW: Import apiClient

// New component to display tiered price details
interface TieredPriceViewerProps {
  tieredPrices: PriceDetail['tiered'];
  title: string;
}

const TieredPriceViewer: React.FC<TieredPriceViewerProps> = ({ tieredPrices, title }) => {
  if (!tieredPrices || Object.keys(tieredPrices).length === 0) {
    return <p className="text-muted-foreground">No tiered prices available for {title}.</p>;
  }
  return (
    <ul className="list-disc list-inside space-y-1">
      {Object.entries(tieredPrices).map(([quantity, tierData]) => (
        <li key={quantity}>
          <span className="font-semibold">{quantity} units:</span>
          <br />Unit Price: ${tierData.unit_price.toFixed(4)}
          <br />Discount: {tierData.discount_perc.toFixed(2)}%
          <br />Total Value: ${tierData.tier_total_value.toFixed(4)}
        </li>
      ))}
    </ul>
  );
};

interface PriceTableProps {
  prices: PriceProduct[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  error: string | null;
  baseEndpoint: string; // NEW: Add baseEndpoint prop
}

const PriceTable: React.FC<PriceTableProps> = ({
  prices,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  loading,
  error,
  baseEndpoint, // NEW: Destructure baseEndpoint
}) => {
  const [exportLoading, setExportLoading] = useState(false); // NEW: State for export loading

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      await apiClient<void>(baseEndpoint, { downloadCsv: true });
    } catch (err: any) {
      // Error already handled by apiClient and globalErrorHandler
    } finally {
      setExportLoading(false);
    }
  };

  if (loading)
    return (
      <p className="text-center text-muted-foreground flex items-center justify-center gap-2"><Spinner size="sm" /> Loading Prices...</p>
    );
  if (error)
    return (
      <p className="text-center text-destructive">Error: {error}</p>
    );
  if (prices.length === 0 && !loading)
    return (
      <p className="text-center text-muted-foreground">No Prices found.</p>
    );

  return (
    <div className="rounded-md border">
      <div className="flex justify-end mb-4"> {/* NEW: Wrapper for export button */}
        <Button onClick={handleExportCsv} disabled={loading || exportLoading} variant="outline">
          {exportLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Exporting...</span> : 'Export to CSV'}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Description</TableHead>
            <TableHead>Article No.</TableHead>
            <TableHead>Vendor</TableHead>
            <TableHead>Family</TableHead>
            <TableHead>Wholesale (Base)</TableHead>
            <TableHead>Wholesale (Final)</TableHead>
            <TableHead>Wholesale (Tiered)</TableHead> {/* New column */}
            <TableHead>Retail (Base)</TableHead>
            <TableHead>Retail (Final)</TableHead>
            <TableHead>Retail (Tiered)</TableHead> {/* New column */}
          </TableRow>
        </TableHeader>
        <TableBody>
          {prices.map((price) => (
            <TableRow key={price.id}>
              <TableCell>{price.description}</TableCell>
              <TableCell>{price.article_number}</TableCell>
              <TableCell>
                {price._labels?.vendor_id || `ID: ${price.vendor_id}`}
              </TableCell>
              <TableCell>
                {price._labels?.family_id || `ID: ${price.family_id}`}
              </TableCell>
              <TableCell>${(price.wholesale as PriceDetail)?.margin?.selling_price_base?.toFixed(4) || 'N/A'}</TableCell>
              <TableCell>${(price.wholesale as PriceDetail)?.margin?.price_total_w_levies?.toFixed(4) || 'N/A'}</TableCell>
              <TableCell> {/* Wholesale Tiered Cell */}
                {typeof price.wholesale === 'object' && (price.wholesale as PriceDetail)?.tiered && Object.keys((price.wholesale as PriceDetail).tiered).length > 0 ? (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="default">
                        View Tiers
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Wholesale Tiered Prices</DialogTitle>
                      </DialogHeader>
                      <TieredPriceViewer tieredPrices={(price.wholesale as PriceDetail).tiered} title="Wholesale" />
                    </DialogContent>
                  </Dialog>
                ) : (
                  'N/A'
                )}
              </TableCell>
              <TableCell>${(price.retail as PriceDetail)?.margin?.selling_price_base?.toFixed(4) || 'N/A'}</TableCell>
              <TableCell>${(price.retail as PriceDetail)?.margin?.price_total_w_levies?.toFixed(4) || 'N/A'}</TableCell>
              <TableCell> {/* Retail Tiered Cell */}
                {typeof price.retail === 'object' && (price.retail as PriceDetail)?.tiered && Object.keys((price.retail as PriceDetail).tiered).length > 0 ? (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="default">
                        View Tiers
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Retail Tiered Prices</DialogTitle>
                      </DialogHeader>
                      <TieredPriceViewer tieredPrices={(price.retail as PriceDetail).tiered} title="Retail" />
                    </DialogContent>
                  </Dialog>
                ) : (
                  'N/A'
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default PriceTable;